import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

// Actions
import { createRoadStretch } from "../../../actions/org/OrgRoadStretchActions";

// Constants and Utils
import { OrgSettingsIndexes } from "../../../constants/GeneralConstants";

// Components
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import { ButtonWithLoader } from "../../../mavin/components/button/Button";

// Sections and Pages
import RoadStretchCreateMapView from "./RoadStretchCreateMapView";
import OrgSettingsSidebar from "../OrgSettingsSidebar";

// CSS
import "../OrgSettings.scss";

/**
 * this function returns route segment ids
 * @param {*} waypointsRoute
 * @returns segmentIds and path
 */
function getWaypointsRouteDetails(waypointsRoute) {
  if (Object.keys(waypointsRoute).length === 0) {
    return { routeSegmentIds: [], routePath: [] };
  }

  const { pathInfoMap, path = [] } = waypointsRoute || {};
  const { road_uid } = pathInfoMap || {};

  if (!road_uid) {
    return { routeSegmentIds: [], routePath: [] };
  }

  const segmentIds = road_uid.map((segment) => segment.value);

  return { routeSegmentIds: segmentIds, routePath: path };
}

// Page Component
// ----------------------------------------------------------------------------
function SelectedSegments({ segmentIds = [] }) {
  const isSegmentsPresent = segmentIds.length > 0;
  const contStyle = isSegmentsPresent
    ? "pb-0 max-h-400 overflow-y-auto"
    : "pb-5";

  return (
    <div className={`mb-5 mt-3 ${contStyle}`}>
      {/* If no Segments are selected  */}
      {!isSegmentsPresent && (
        <p className="font-italic">{"Use the map to search the Route"}</p>
      )}

      {/* If at least one segment is selected */}
      {isSegmentsPresent && (
        <>
          <strong>{`Road Segments Selected  (Total - ${segmentIds.length})`}</strong>
          <ul>
            {segmentIds.map((segmentId) => {
              return (
                <li key={segmentId} className="p-1">
                  {segmentId}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function StretchNameInputSection({ stretchName, setStretchName }) {
  return (
    <div className="form-group mb-2 py-3">
      <label className="control-label">{"Road Stretch Name"}</label>
      <input
        className="form-control rounded-lg"
        placeholder="Enter Stretch Name"
        id={"StretchNameStr"}
        value={stretchName}
        onChange={(e) => setStretchName(e.target.value)}
      />
    </div>
  );
}

function FooterSection({ stretchName = "", segmentIds = [], navPath = [] }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const createStretchLoading = useSelector(
    (state) => state.orgRoadStretch.createStretchLoading
  );

  const isLoading = createStretchLoading;
  const isDisabled = createStretchLoading || !stretchName || !segmentIds.length;

  // functions
  function saveStretch() {
    dispatch(createRoadStretch(stretchName, segmentIds, navPath, history));
  }

  return (
    <>
      <hr className="divider my-3"></hr>
      <div className="text-right mr-3">
        <ButtonWithLoader
          className="btn btn-primary shadow-none shrink-0 px-3"
          displayContent="Save Route"
          loader={isLoading}
          isDisabled={isDisabled}
          onClickFunction={saveStretch}
        />
      </div>
    </>
  );
}

/**
 * Page Component
 */
export default function RoadStretchCreatePage() {
  // State
  const [stretchName, setStretchName] = useState("");

  // waypoints route
  const waypointsRoute = useSelector(
    (state) => state.orgRoadStretch.waypointsRoute
  );

  // route segment Ids and Path
  const { routeSegmentIds, routePath } =
    getWaypointsRouteDetails(waypointsRoute);

  // Render Page
  return (
    <>
      <OrgSettingsSidebar activeIdx={OrgSettingsIndexes.roadStretches} />

      <div className="content-wrapper map-content-wrapper">
        {/** Map Layout :: Left */}
        <div className="map-layout-left">
          <PageHeader
            title={"New Road Stretch"}
            shadow={false}
            border={true}
            className="pb-2"
          />

          {/* Input for Stretch Name */}
          {routeSegmentIds.length > 0 && (
            <StretchNameInputSection
              stretchName={stretchName}
              setStretchName={setStretchName}
            />
          )}

          {/* Section to display segment names/ids */}
          <SelectedSegments segmentIds={routeSegmentIds} />

          {/* Section for "save" and "clear selection" buttons*/}
          <FooterSection
            stretchName={stretchName}
            segmentIds={routeSegmentIds}
            navPath={routePath}
          />
        </div>

        {/** Map Section */}
        <RoadStretchCreateMapView waypointsRoute={waypointsRoute} />
      </div>
    </>
  );
}
